/**
 * Timeline date utilities — pure functions for column generation and
 * date-range computation. Kept separate so they are testable.
 */

export function startOfDayLocal(d: Date): Date {
  const out = new Date(d)
  out.setHours(0, 0, 0, 0)
  return out
}

export function addDays(d: Date, n: number): Date {
  const out = new Date(d)
  out.setDate(out.getDate() + n)
  return out
}

export function addMonths(d: Date, n: number): Date {
  const out = new Date(d)
  out.setMonth(out.getMonth() + n)
  return out
}

/** Monday of the ISO week containing `d` */
export function getMonday(d: Date): Date {
  const dt = new Date(d)
  const day = dt.getDay()
  const diff = (day + 6) % 7 // 0 (Sun) → 6, 1 (Mon) → 0, …
  dt.setDate(dt.getDate() - diff)
  return startOfDayLocal(dt)
}

export function isoWeekNumber(d: Date): number {
  const dt = new Date(d)
  dt.setHours(0, 0, 0, 0)
  dt.setDate(dt.getDate() + 3 - ((dt.getDay() + 6) % 7))
  const week1 = new Date(dt.getFullYear(), 0, 4)
  return 1 + Math.round(((dt.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
}

export type ZoomLevel = 'days' | 'weeks' | 'months' | 'quarters'

export interface TimelineCol {
  id: string
  label: string
  startDate: Date
  endDate: Date // exclusive
}

export function buildDayCols(from: Date, to: Date): TimelineCol[] {
  const cols: TimelineCol[] = []
  const cursor = startOfDayLocal(from)
  const end = startOfDayLocal(to)
  while (cursor < end) {
    const next = addDays(cursor, 1)
    cols.push({
      id: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`,
      label: cursor.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      startDate: new Date(cursor),
      endDate: new Date(next),
    })
    cursor.setTime(next.getTime())
  }
  return cols
}

export function getWeekInMonthLabel(d: Date): string {
  const wk = Math.ceil(d.getDate() / 7)
  const month = d.toLocaleDateString('en-US', { month: 'short' })
  return `W${wk}-${month}`
}

export function buildWeekCols(from: Date, to: Date): TimelineCol[] {
  const cols: TimelineCol[] = []
  const cursor = getMonday(from)
  const end = startOfDayLocal(to)
  while (cursor < end) {
    const next = addDays(cursor, 7)
    const label = getWeekInMonthLabel(cursor)
    cols.push({
      id: `w-${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`,
      label,
      startDate: new Date(cursor),
      endDate: new Date(next),
    })
    cursor.setTime(next.getTime())
  }
  return cols
}

export function buildMonthCols(from: Date, to: Date): TimelineCol[] {
  const cols: TimelineCol[] = []
  const start = new Date(from.getFullYear(), from.getMonth(), 1)
  const end = startOfDayLocal(to)
  let cursor = start
  while (cursor < end) {
    const next = addMonths(cursor, 1)
    const nextStart = new Date(next.getFullYear(), next.getMonth(), 1)
    cols.push({
      id: `m-${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`,
      label: cursor.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      startDate: new Date(cursor),
      endDate: new Date(nextStart),
    })
    cursor.setTime(nextStart.getTime())
  }
  return cols
}

export function buildQuarterCols(from: Date, to: Date): TimelineCol[] {
  const cols: TimelineCol[] = []
  const start = new Date(from.getFullYear(), Math.floor(from.getMonth() / 3) * 3, 1)
  const end = startOfDayLocal(to)
  let cursor = start
  while (cursor < end) {
    const q = Math.floor(cursor.getMonth() / 3)
    const nextQ = q + 1
    const nextYear = nextQ > 3 ? cursor.getFullYear() + 1 : cursor.getFullYear()
    const nextQStart = new Date(nextYear, (nextQ % 4) * 3, 1)
    const label = `Q${q + 1} ${cursor.getFullYear()}`
    cols.push({
      id: `q-${cursor.getFullYear()}-${q}`,
      label,
      startDate: new Date(cursor),
      endDate: new Date(nextQStart),
    })
    cursor.setTime(nextQStart.getTime())
  }
  return cols
}

/**
 * Compute the visible timeline window.
 *
 * Priority:
 * 1. Earliest/latest task dates (startDate + dueDate)
 * 2. Sprint dates (if available)
 * 3. Today
 * 4. Zoom-appropriate padding
 */
/**
 * Parses a date value safely to local midnight Date, avoiding UTC shift on YYYY-MM-DD strings.
 */
export function parseLocalDate(d: Date | string | null | undefined): Date | null {
  if (!d) return null
  if (d instanceof Date) return startOfDayLocal(d)
  if (typeof d === 'string') {
    const match = d.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (match) {
      const year = parseInt(match[1], 10)
      const month = parseInt(match[2], 10) - 1
      const day = parseInt(match[3], 10)
      return new Date(year, month, day, 0, 0, 0, 0)
    }
    const parsed = new Date(d)
    if (!isNaN(parsed.getTime())) {
      return startOfDayLocal(parsed)
    }
  }
  return null
}

export function computeTimelineRange(
  zoom: 'days' | 'weeks' | 'months' | 'quarters',
  issues: Array<{ startDate?: string | null; start_date?: string | null; due_date?: string | null }>,
  space: any,
): { from: Date; to: Date } {
  const today = startOfDayLocal(new Date())

  const taskDates: Date[] = []
  for (const issue of issues) {
    const s = issue.startDate || issue.start_date || issue.due_date
    const d = issue.due_date
    const parsedS = parseLocalDate(s)
    const parsedD = parseLocalDate(d)
    if (parsedS) taskDates.push(parsedS)
    if (parsedD) {
      taskDates.push(parsedD)
      taskDates.push(addDays(parsedD, 1))
    }
  }

  const sprintDates: Date[] = []
  for (const s of space?.sprints ?? []) {
    if (s.startDate) {
      const ps = parseLocalDate(s.startDate)
      if (ps) sprintDates.push(ps)
    }
    if (s.endDate) {
      const pe = parseLocalDate(s.endDate)
      if (pe) {
        sprintDates.push(pe)
        sprintDates.push(addDays(pe, 1))
      }
    }
  }

  const allDates = [...taskDates, ...sprintDates, today]

  let minDate = allDates.length > 0
    ? new Date(Math.min(...allDates.map((d) => d.getTime())))
    : addMonths(today, -1)
  let maxDate = allDates.length > 0
    ? new Date(Math.max(...allDates.map((d) => d.getTime())))
    : addMonths(today, 1)

  if (zoom === 'days') {
    minDate = addDays(minDate, -7)
    maxDate = addDays(maxDate, 14)
  } else if (zoom === 'weeks') {
    minDate = getMonday(minDate)
    minDate = addDays(minDate, -7)
    maxDate = addDays(maxDate, 21)
  } else if (zoom === 'months') {
    minDate = new Date(minDate.getFullYear(), minDate.getMonth() - 1, 1)
    maxDate = addMonths(maxDate, 4)
    maxDate = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 1)
  } else {
    // Quarters: snap to quarter boundaries, ±1 quarter padding
    const minQ = Math.floor(minDate.getMonth() / 3)
    const maxQ = Math.floor(maxDate.getMonth() / 3)
    minDate = new Date(minDate.getFullYear(), minQ * 3, 1)
    maxDate = new Date(maxDate.getFullYear(), (maxQ + 1) * 3, 1)
  }

  return { from: startOfDayLocal(minDate), to: startOfDayLocal(maxDate) }
}

export const ZOOM_COL_WIDTH: Record<ZoomLevel, number> = {
  days: 48,
  weeks: 110,
  months: 150,
  quarters: 200,
}

/**
 * Maps any date onto the timeline's horizontal pixel coordinate space.
 * Each column has a fixed pixel width (colWidth). Dates within a column
 * are positioned linearly across that column's exact start and end times.
 */
export function dateToPixel(
  d: Date | string | null | undefined,
  timelineCols: TimelineCol[],
  colWidth: number,
): number {
  if (!d || timelineCols.length === 0) return -200
  const dt = d instanceof Date ? d : parseLocalDate(d)
  if (!dt) return -200
  const t = dt.getTime()

  const firstCol = timelineCols[0]
  if (t <= firstCol.startDate.getTime()) {
    const colMs = firstCol.endDate.getTime() - firstCol.startDate.getTime()
    const offset = ((t - firstCol.startDate.getTime()) / colMs) * colWidth
    return offset
  }

  const lastCol = timelineCols[timelineCols.length - 1]
  if (t >= lastCol.endDate.getTime()) {
    const colMs = lastCol.endDate.getTime() - lastCol.startDate.getTime()
    const offset = ((t - lastCol.endDate.getTime()) / colMs) * colWidth
    return timelineCols.length * colWidth + offset
  }

  for (let i = 0; i < timelineCols.length; i++) {
    const col = timelineCols[i]
    if (t >= col.startDate.getTime() && t < col.endDate.getTime()) {
      const colMs = col.endDate.getTime() - col.startDate.getTime()
      const frac = (t - col.startDate.getTime()) / colMs
      return i * colWidth + frac * colWidth
    }
  }

  return 0
}

export interface TaskBarPosition {
  leftPx: number
  widthPx: number
  visible: boolean
}

/**
 * Calculates a task bar's visual left position and width from the task's
 * actual startDate and dueDate mapped onto the current timeline column scale.
 * 
 * Switching zoom levels changes only the column scale (colWidth and columns),
 * leaving the task's dates and schedule completely untouched.
 */
export function calculateTaskBar(
  issue: {
    startDate?: string | null
    start_date?: string | null
    dueDate?: string | null
    due_date?: string | null
    [key: string]: any
  },
  timelineCols: TimelineCol[],
  colWidth: number,
): TaskBarPosition {
  const rawStart = issue.startDate || issue.start_date || issue.due_date || issue.dueDate
  const rawEnd = issue.due_date || issue.dueDate || issue.startDate || issue.start_date

  const parsedStart = parseLocalDate(rawStart)
  const parsedEnd = parseLocalDate(rawEnd)

  if (!parsedStart || !parsedEnd || timelineCols.length === 0) {
    return { leftPx: -200, widthPx: 0, visible: false }
  }

  // Ensure actualEnd is at or after actualStart
  const actualStart = parsedStart
  const actualEnd = parsedEnd < parsedStart ? parsedStart : parsedEnd

  // The bar covers through the entire end of the due date (inclusive: end of that day)
  const endInclusive = addDays(actualEnd, 1)

  const leftPx = dateToPixel(actualStart, timelineCols, colWidth)
  const rightPx = dateToPixel(endInclusive, timelineCols, colWidth)
  let widthPx = rightPx - leftPx

  // A 1-day task or milestone should at least be 1 full column width (or minimum 24px)
  const minWidth = Math.max(24, Math.min(colWidth, 48))
  if (widthPx < minWidth) {
    widthPx = minWidth
  }

  return {
    leftPx,
    widthPx,
    visible: true,
  }
}

